import type { ItemLite } from "@plannello/shared";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { ProductView } from "@/components/items/ProductView";
import { validateViewSearch } from "@/components/items/viewSearch";
import { useCatalog } from "@/data/CatalogProvider";
import { useProductItems } from "@/data/items";
import { litesKey } from "@/data/store";

/*
 * Dev-only performance bench (English by design, not linked anywhere): /dev/perf?product=NWS&n=2000
 * Clones the product's cached rows into N synthetic ones in the client cache (nothing is written
 * to the server), renders the real list, and measures view switches and scrolling.
 */
export const Route = createFileRoute("/_app/dev/perf")({
  validateSearch: (s: Record<string, unknown>) => ({
    ...validateViewSearch(s),
    product: typeof s.product === "string" ? s.product : "NWS",
    n: Number(s.n) > 0 ? Number(s.n) : 2000,
    layout: s.layout === "timeline" ? ("timeline" as const) : ("list" as const),
  }),
  component: Perf,
});

const day = (offset: number) => new Date(Date.now() + offset * 86_400_000).toISOString().slice(0, 10);
const WORDS = ["checkout", "search", "refund", "invoice", "cart", "login", "export", "webhook", "catalog", "price"];
const VERBS = ["Fix", "Add", "Speed up", "Refactor", "Document", "Translate", "Remove", "Audit"];

function Perf() {
  const search = Route.useSearch();
  const qc = useQueryClient();
  const catalog = useCatalog();
  const navigate = useNavigate();
  const product = catalog.productByKey.get(search.product.toUpperCase());
  const base = useProductItems(product ? [product.id] : []);
  const [ready, setReady] = useState(false);
  const [results, setResults] = useState<string[]>([]);

  useEffect(() => {
    if (!product || base.isPending || ready) return;
    const seed = base.items.length ? base.items : [];
    if (seed.length === 0) return;
    const statuses = catalog.statusesOf(product.workflowId);
    const labels = catalog.schemaOf(product.id)?.labels ?? [];
    const users = catalog.users;
    const rows: ItemLite[] = [];
    for (let i = 0; i < search.n; i++) {
      const s = seed[i % seed.length]!;
      rows.push({
        ...s,
        id: crypto.randomUUID(),
        key: `${product.key}-${10000 + i}`,
        number: 10000 + i,
        title: `${VERBS[i % VERBS.length]} ${WORDS[(i * 7) % WORDS.length]} flow ${i}`,
        statusId: statuses[i % statuses.length]?.id ?? s.statusId,
        priority: i % 5,
        labelIds: labels.length ? [labels[i % labels.length]!.id] : [],
        assigneeIds: users.length ? [users[i % users.length]!.id] : [],
        // Every fifth row is a parent of the next four (tree rows), a third have dates (timeline).
        parentId: i % 5 ? (rows[i - (i % 5)]?.id ?? null) : null,
        startDate: i % 3 === 0 ? day(((i * 7) % 240) - 60) : null,
        dueDate: i % 3 === 0 ? day(((i * 7) % 240) - 60 + 3 + (i % 12)) : null,
        rank: `a${String(i).padStart(6, "0")}`,
        updatedAt: new Date(Date.now() - i * 3_600_000).toISOString(),
      });
    }
    qc.setQueryData(litesKey(product.id), rows);
    setReady(true);
  }, [product, base, ready, catalog, qc, search.n]);

  const time = async (label: string, change: () => void) => {
    const t0 = performance.now();
    change();
    await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
    const ms = performance.now() - t0;
    setResults((r) => [...r, `${label}: ${ms.toFixed(0)} ms`]);
  };

  const scrollTest = async () => {
    const el = document.querySelector<HTMLElement>('main [role="treegrid"]')?.parentElement;
    if (!el) return;
    const frames: number[] = [];
    let last = performance.now();
    let running = true;
    const tick = (t: number) => {
      frames.push(t - last);
      last = t;
      if (running) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
    const total = el.scrollHeight - el.clientHeight;
    for (let y = 0; y <= total; y += 240) {
      el.scrollTop = y;
      await new Promise((r) => requestAnimationFrame(r));
    }
    running = false;
    const slow = frames.filter((f) => f > 20).length;
    const avg = frames.reduce((a, b) => a + b, 0) / Math.max(1, frames.length);
    setResults((r) => [...r, `scroll ${frames.length} frames: avg ${avg.toFixed(1)} ms, ${slow} over 20 ms`]);
  };

  const go = (patch: Record<string, string | undefined>) =>
    navigate({ to: ".", search: ((p: Record<string, unknown>) => ({ ...p, ...patch })) as never, replace: true });

  if (!product) return <p className="p-8 text-sm">Unknown product {search.product}</p>;
  if (!ready) return <p className="p-8 text-sm">Preparing {search.n} rows</p>;

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-line px-4 py-2 text-xs">
        <span className="font-medium">Perf bench, {search.n} rows</span>
        <button
          type="button"
          className="rounded-control border border-line px-2 py-1"
          onClick={() => void time("group by priority", () => go({ group: "priority" }))}
        >
          Group priority
        </button>
        <button
          type="button"
          className="rounded-control border border-line px-2 py-1"
          onClick={() => void time("group by assignee", () => go({ group: "assignee" }))}
        >
          Group assignee
        </button>
        <button
          type="button"
          className="rounded-control border border-line px-2 py-1"
          onClick={() => void time("group by status", () => go({ group: undefined }))}
        >
          Group status
        </button>
        <button
          type="button"
          className="rounded-control border border-line px-2 py-1"
          onClick={() => void time("filter priority:p0", () => go({ q: "priority:p0" }))}
        >
          Filter p0
        </button>
        <button
          type="button"
          className="rounded-control border border-line px-2 py-1"
          onClick={() => void time("clear filter", () => go({ q: undefined }))}
        >
          Clear
        </button>
        <button
          type="button"
          className="rounded-control border border-line px-2 py-1"
          onClick={() => void time("sub-group by type", () => go({ sub: "type" }))}
        >
          Sub-group type
        </button>
        <button
          type="button"
          className="rounded-control border border-line px-2 py-1"
          onClick={() => void time("timeline", () => go({ layout: "timeline", rows: "all" }))}
        >
          Timeline
        </button>
        <button
          type="button"
          className="rounded-control border border-line px-2 py-1"
          onClick={() => void scrollTest()}
        >
          Scroll test
        </button>
        <output data-perf-results className="ml-auto font-mono tnum">
          {results.join(" | ")}
        </output>
      </div>
      <div className="min-h-0 flex-1">
        <ProductView productKey={product.key} layout={search.layout} search={search} />
      </div>
    </div>
  );
}
